import { chatExtras } from './chat.ts';
import type { ClosedGuild, DropListing, GroupBuy, GuildCard, GuildMember, GuildFeedItem, GuildMessage, GuildRole, GuildsPort, GuildTransfer, GuildView, SellerRequest, SharedPost } from './ports.ts';
import type { WorkerClient } from './worker.ts';

type Row = { id: string; data: Record<string, unknown> };
type Op = '==' | 'in';
type QueryOptions = { filters?: [string, Op, unknown][]; orderBy?: [string, 'asc' | 'desc']; limit?: number };

export type GuildSources = {
  worker: WorkerClient;
  currentUid: () => string | null;
  readDocument: (collection: string, id: string) => Promise<Record<string, unknown> | null>;
  query: (collection: string, options: QueryOptions) => Promise<Row[]>;
  watchQuery: (collection: string, options: QueryOptions, onData: (rows: Row[]) => void, onError: (error: Error) => void) => () => void;
  watchTyping: (guildId: string, listener: (typing: Record<string, number>) => void) => () => void;
  markTyping: (guildId: string, uid: string) => Promise<void>;
};

const ROLES: GuildRole[] = ['owner', 'mod', 'seller', 'member'];
const TYPING_FRESH_MS = 6000;
const TYPING_EVERY_MS = 3000;
const text = (value: unknown) => (typeof value === 'string' ? value : '');
const media = (value: unknown) => {
  const url = text(value);
  return url.startsWith('https://') || url.startsWith('http://127.0.0.1:') ? url : null;
};
const role = (value: unknown): GuildRole => (ROLES.includes(value as GuildRole) ? value as GuildRole : 'member');

function listing(value: unknown): DropListing | null {
  if (!value || typeof value !== 'object') return null;
  const data = value as Record<string, unknown>;
  return {
    id: text(data.id), name: text(data.name) || 'Listing', priceMinor: Math.round(Number(data.priceMinor) || 0), photo: media(data.photo),
    shopId: text(data.shopId), shopName: text(data.shopName), shopHandle: text(data.shopHandle) || null,
  };
}

export function sharedPost(value: unknown): SharedPost | null {
  if (!value || typeof value !== 'object') return null;
  const post = value as Record<string, unknown>;
  return text(post.id) ? { id: text(post.id), caption: text(post.caption), photo: media(post.photo), authorName: text(post.authorName) || 'Member', video: post.video === true } : null;
}

export function guildMessageFromRecord(id: string, data: Record<string, unknown>): GuildMessage {
  const kind = ['text', 'drop', 'groupbuy', 'system', 'announcement', 'post'].includes(text(data.kind)) ? data.kind as GuildMessage['kind'] : 'text';
  const author = data.author && typeof data.author === 'object' ? data.author as Record<string, unknown> : null;
  const reply = data.replyTo && typeof data.replyTo === 'object' ? data.replyTo as Record<string, unknown> : null;
  return {
    id, kind, authorId: text(data.authorId) || null,
    author: author ? { name: text(author.name) || 'Member', handle: text(author.handle) || null, photoUrl: media(author.photoUrl), role: text(author.role) || 'member' } : null,
    text: text(data.text), replyTo: reply ? { id: text(reply.id), authorName: text(reply.authorName), text: text(reply.text) } : null,
    listing: listing(data.listing), groupBuyId: text(data.groupBuyId) || null, post: sharedPost(data.post), heartCount: Math.max(0, Number(data.heartCount) || 0),
    scamWarning: data.scamWarning === true, createdAt: text(data.createdAt),
    mentions: Array.isArray(data.mentions) ? data.mentions.map(String) : [], seenCount: Math.max(0, Number(data.seenCount) || 0),
    ...chatExtras(data),
  };
}

function guildFromRecord(id: string, data: Record<string, unknown>): GuildView {
  return {
    id, name: text(data.name) || 'Guild', about: text(data.about), rules: Array.isArray(data.rules) ? data.rules.map(String) : [], perk: text(data.perk),
    bannerUrl: media(data.bannerUrl), memberCount: Math.max(0, Number(data.memberCount) || 0), ownerId: text(data.ownerId), pinnedMessageId: text(data.pinnedMessageId) || null,
    isPublic: data.public !== false,
    status: data.status === 'closed' ? 'closed' : 'active', closedAt: text(data.closedAt) || null, purgeAfter: text(data.purgeAfter) || null,
  };
}

function groupBuyFromRecord(id: string, data: Record<string, unknown>): GroupBuy {
  const status = data.status === 'unlocked' || data.status === 'closed' ? data.status : 'open';
  return {
    id, guildId: text(data.guildId), listing: listing(data.listing) ?? { id: '', name: 'Listing', priceMinor: 0, photo: null, shopId: '', shopName: '', shopHandle: null },
    target: Math.max(1, Number(data.target) || 1), joined: Math.max(0, Number(data.joined) || 0), perk: text(data.perk), status, endsAt: text(data.endsAt), createdBy: text(data.createdBy),
  };
}

export function createGuilds(sources: GuildSources): GuildsPort {
  const { worker, currentUid, readDocument, query, watchQuery } = sources;
  let lastTyping = 0;
  const port: GuildsPort = {
    async discover(search = '') {
      const rows = await query('guilds', { filters: [['v', '==', 2], ['status', '==', 'active']], orderBy: ['memberCount', 'desc'], limit: 100 });
      const words = search.toLowerCase().split(/\s+/).filter(Boolean);
      return rows.filter(({ data }) => data.public !== false).map(({ id, data }): GuildCard => ({ id, name: text(data.name), about: text(data.about), memberCount: Number(data.memberCount) || 0, bannerUrl: media(data.bannerUrl) }))
        .filter(guild => words.every(word => `${guild.name} ${guild.about}`.toLowerCase().includes(word)));
    },
    async mine(options) {
      const uid = currentUid();
      const record = uid ? await readDocument('guildMemberships', uid).catch((error: unknown) => {
        if (options?.strict) throw error;
        return null;
      }) : null;
      const guilds = (record?.guilds ?? {}) as Record<string, { role?: unknown; name?: unknown; closed?: unknown }>;
      // Deleted guilds stay in the list (for restore) but are never shown (V2-049).
      return Object.entries(guilds).filter(([, entry]) => entry?.closed !== true).map(([id, entry]) => ({ id, name: text(entry?.name) || id, role: role(entry?.role) })).sort((left, right) => left.name.localeCompare(right.name));
    },
    async guild(guildId) {
      const data = await readDocument('guilds', guildId).catch(() => null);
      return data ? guildFromRecord(guildId, data) : null;
    },
    async myRole(guildId) {
      const uid = currentUid();
      const member = uid ? await readDocument(`guilds/${guildId}/members`, uid).catch(() => null) : null;
      return member ? role(member.role) : null;
    },
    async members(guildId) {
      const rows = await query(`guilds/${guildId}/members`, { limit: 300 });
      const order = (value: GuildRole) => ROLES.indexOf(value);
      return rows.map(({ id, data }): GuildMember => ({
        uid: id, name: text(data.name) || 'Member', handle: text(data.handle) || null, photoUrl: media(data.photoUrl), role: role(data.role), mutedUntil: text(data.mutedUntil) || null,
      })).sort((left, right) => order(left.role) - order(right.role) || left.name.localeCompare(right.name));
    },
    // The newest 150 messages, shown oldest first.
    watchMessages: (guildId, listener, onError) => watchQuery(`guilds/${guildId}/messages`, { orderBy: ['createdAt', 'desc'], limit: 150 },
      rows => listener(rows.filter(row => row.data.status !== 'removed').map(row => guildMessageFromRecord(row.id, row.data)).reverse()), onError),
    async heartedIds(guildId, messageIds) {
      const uid = currentUid();
      if (!uid) return new Set();
      const found = await Promise.all(messageIds.map(async id => ((await readDocument(`guilds/${guildId}/messages/${id}/hearts`, uid).catch(() => null)) ? id : null)));
      return new Set(found.filter((id): id is string => Boolean(id)));
    },
    watchTyping(guildId, listener) {
      return sources.watchTyping(guildId, typing => {
        const me = currentUid();
        const now = Date.now();
        listener(Object.entries(typing).filter(([uid, at]) => uid !== me && now - Number(at) < TYPING_FRESH_MS).map(([uid]) => uid));
      });
    },
    typing(guildId) {
      const uid = currentUid();
      if (!uid || Date.now() - lastTyping < TYPING_EVERY_MS) return;
      lastTyping = Date.now();
      void sources.markTyping(guildId, uid).catch(() => undefined);
    },
    async groupBuys(guildId) {
      const rows = await query('groupBuys', { filters: [['guildId', '==', guildId]], orderBy: ['createdAt', 'desc'], limit: 50 });
      return rows.map(row => groupBuyFromRecord(row.id, row.data));
    },
    async joinedGroupBuys(groupBuyIds) {
      const uid = currentUid();
      if (!uid) return new Set();
      const found = await Promise.all(groupBuyIds.map(async id => ((await readDocument(`groupBuys/${id}/joined`, uid).catch(() => null)) ? id : null)));
      return new Set(found.filter((id): id is string => Boolean(id)));
    },
    async sellerRequests(guildId) {
      const rows = await query(`guilds/${guildId}/sellerRequests`, { filters: [['status', '==', 'pending']], limit: 100 });
      return rows.map(({ id, data }): SellerRequest => ({
        uid: id, name: text(data.name), handle: text(data.handle) || null, shopId: text(data.shopId), shopName: text(data.shopName), note: text(data.note), status: text(data.status), createdAt: text(data.createdAt),
      }));
    },
    create: ({ isPublic = true, ...input }) => worker.call('create_guild', { ...input, public: isPublic }),
    update: async (guildId, patch) => { await worker.call('update_guild', { guildId, ...patch }); },
    join: async (guildId, inviteCode) => { await worker.call('join_guild', { guildId, ...(inviteCode ? { inviteCode } : {}) }); },
    inviteCode: async guildId => (await worker.call<{ inviteCode: string | null }>('get_guild_invite', { guildId })).inviteCode ?? null,
    inviteMember: (guildId, handle) => worker.call<{ handle: string; duplicate: boolean }>('invite_to_guild', { guildId, handle }),
    startTransfer: async (guildId, uid, afterwards, note) => (await worker.call<{ transfer: GuildTransfer }>('start_guild_transfer', { guildId, uid, afterwards, note })).transfer,
    cancelTransfer: async guildId => { await worker.call('cancel_guild_transfer', { guildId }); },
    getTransfer: async guildId => (await worker.call<{ transfer: GuildTransfer | null }>('get_guild_transfer', { guildId })).transfer,
    respondToTransfer: (guildId, accept) => worker.call<{ status: string }>('respond_guild_transfer', { guildId, accept }),
    close: (guildId, confirmName) => worker.call<{ purgeAfter: string }>('close_guild', { guildId, confirmName }),
    restore: async guildId => { await worker.call('restore_guild', { guildId }); },
    myClosed: async () => (await worker.call<{ guilds: ClosedGuild[] }>('my_closed_guilds')).guilds,
    onlineCount: async guildId => {
      const result = await worker.call<{ online: number | null }>('guild_online_count', { guildId });
      return typeof result.online === 'number' ? result.online : null;
    },
    leave: async guildId => { await worker.call('leave_guild', { guildId }); },
    send: (guildId, value, replyTo, attachments = {}) => worker.call('send_guild_message', { guildId, text: value, ...attachments, ...(replyTo ? { replyTo } : {}) }),
    markRead: async guildId => { await worker.call('mark_guild_read', { guildId }); },
    heart: async (guildId, messageId, hearted) => { await worker.call('set_guild_heart', { guildId, messageId, hearted }); },
    deleteMessage: async (guildId, messageId) => { await worker.call('delete_guild_message', { guildId, messageId }); },
    pin: async (guildId, messageId) => { await worker.call('pin_guild_message', { guildId, messageId }); },
    mute: async (guildId, uid, muted) => { await worker.call('mute_guild_member', { guildId, uid, muted }); },
    remove: async (guildId, uid) => { await worker.call('remove_guild_member', { guildId, uid }); },
    setRole: async (guildId, uid, value) => { await worker.call('set_guild_role', { guildId, uid, role: value }); },
    requestSeller: (guildId, shopId, note) => worker.call('request_guild_seller', { guildId, shopId, note }),
    respondSeller: async (guildId, uid, approve) => { await worker.call('respond_guild_seller', { guildId, uid, approve }); },
    sharePost: async (guildId, postId) => { await worker.call('share_post_to_guild', { guildId, postId }); },
    async feed(options) {
      const mine = await port.mine(options?.kind === 'drop' ? { strict: true } : undefined);
      const guilds = options?.kind === 'drop' ? mine : mine.slice(0, 15);
      const kinds = options?.kind === 'drop' ? ['drop'] as const : ['drop', 'post'] as const;
      const lists = await Promise.all(guilds.flatMap(guild => kinds.map(kind =>
        query(`guilds/${guild.id}/messages`, { filters: [['kind', '==', kind]], orderBy: ['createdAt', 'desc'], limit: 8 })
          .then(rows => rows.filter(row => row.data.status !== 'removed').map((row): GuildFeedItem => ({ guildId: guild.id, guildName: guild.name, message: guildMessageFromRecord(row.id, row.data) })))
          .catch((error: unknown) => {
            // The hub offers Retry instead of reporting a failed read as an
            // empty feed. Keep Home's existing best-effort behaviour.
            if (options?.kind === 'drop') throw error;
            return [] as GuildFeedItem[];
          }))));
      return lists.flat().sort((left, right) => right.message.createdAt.localeCompare(left.message.createdAt)).slice(0, 40);
    },
    shareDrop: async (guildId, listingId, value) => { await worker.call('share_drop', { guildId, listingId, text: value }); },
    startGroupBuy: async (guildId, input) => { await worker.call('start_group_buy', { guildId, ...input }); },
    joinGroupBuy: (groupBuyId, joined) => worker.call('join_group_buy', { groupBuyId, joined }),
  };
  return port;
}
